import { ConfirmDialogComponent } from '@/components/confirm-dialog/confirm-dialog.component';
import { RadioComponent } from '@/components/ui-forms/radio/radio.component';
import { SelectComponent } from '@/components/ui-forms/select/select.component';
import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { EnumHelper } from '@/helpers/enumHelper';
import { UserHelper } from '@/helpers/user.helper';
import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import {
  PropertiesBookletService,
  PropertyBookletCreate,
  PropertyBookletRenovationWorkType,
  PropertyBookletType,
  RealProperty
} from '@eemo/api-client';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-property-booklet-create',
  standalone: true,
  imports: [
    NgIf,
    MatCardModule,
    TranslateModule,
    MatRadioModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    SelectComponent,
    RadioComponent,
    TooltipDirective
  ],
  templateUrl: './property-booklet-create.component.html',
  styleUrl: './property-booklet-create.component.scss'
})
export class PropertyBookletCreateComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;
  @Output() bookletCreated = new EventEmitter<string>();

  PropertyBookletType = PropertyBookletType;

  loading = false;
  sub = new Subscription();
  canUpdateProperty = false;
  createTooltip = 'Créer un CIL';

  renovationWorkTypeList: PropertyBookletRenovationWorkType[] = EnumHelper.getApiEnumList(
    PropertyBookletRenovationWorkType
  );

  creationForm = this.fb.group({
    creationType: [PropertyBookletType.BUILDING, Validators.required],
    renovationWorkTypes: [null]
  });

  get form_creationType() {
    return this.creationForm.get('creationType') as FormControl<PropertyBookletType>;
  }
  get form_renovationWorkTypes() {
    return this.creationForm.get('renovationWorkTypes') as FormControl<PropertyBookletRenovationWorkType[]>;
  }

  constructor(
    private bookletService: PropertiesBookletService,
    private dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private userHelper: UserHelper
  ) {}

  ngOnInit(): void {
    this.form_creationType.valueChanges.subscribe((value) => {
      if (value === PropertyBookletType.BUILDING) {
        //remove validators on renovationWorkTypes
        this.form_renovationWorkTypes.clearValidators();
      } else {
        // add validators
        this.form_renovationWorkTypes.addValidators([Validators.required, Validators.minLength(1)]);
        this.form_renovationWorkTypes.setValue(Object.values(PropertyBookletRenovationWorkType));
      }
    });
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    if (!this.canUpdateProperty) {
      this.createTooltip = 'Vous ne pouvez pas modifier ce bien';
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  confirmCreateBooklet() {
    this.creationForm.updateValueAndValidity();
    if (this.creationForm.invalid) {
      return;
    }
    // prevents to open multiple dialog
    const typeText = this.translate.instant('enum.PropertyBookletType.' + this.form_creationType.value);
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: `Confirmez-vous la création d'un CIL pour une ${typeText} ?`
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doCreateBooklet();
        }
      });
    }
  }

  doCreateBooklet() {
    this.loading = true;
    const bookletCreate: PropertyBookletCreate = {
      type: this.form_creationType.value,
      renovationWorkTypes: this.form_renovationWorkTypes.value
    };
    this.sub = this.bookletService.createRealPropertyBooklet(this.prop.uid, bookletCreate).subscribe({
      next: (createId) => {
        this.bookletCreated.emit(createId);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }
}
